import Image from 'next/image';
import { useContext, useState } from 'react';

import { MarkdownRichText } from '@/app/components/shared';
import { RichText, SectionHeader } from '@swordhealth/ui-corporate';
import {
  ButtonWrapper,
  Card,
  CardHoverContent,
  CardHoverInner,
  CardInner,
  CloseButton,
  HeaderWrapper,
  ImageWrapper,
  SliderWrapper,
  StyledContainer,
  StyledSlider,
  Wrapper,
} from './styles';
import useBetterMediaQuery from '@/utils/useBetterMediaQuery';
import getImgSrcWorkaround from '@/utils/images';
import ArrowSVG from '/public/assets/arrow-round-button.svg';
import IconCloseSVG from '/public/icons/circle-close.svg';
import ClientContext from '@/contexts/client.context';
import EnrollButton from '../../core/EnrollButton';
import theme from '@/utils/styles-variables';

const HoverCardsSection = ({ content }) => {
  const { openModal, client } = useContext(ClientContext);
  const [isHovered, setIsHovered] = useState(null);
  const isDesktop = useBetterMediaQuery(`(min-width: ${theme.breakpoints.min_xl}px)`);
  const isTablet = useBetterMediaQuery(
    `(min-width: ${theme.breakpoints.min_md}px) and (max-width: ${theme.breakpoints.max_md}px)`,
  );
  const isMobile = useBetterMediaQuery(`(max-width: ${theme.breakpoints.max_sm}px)`);

  const {
    sectionHeader: { title, upperLabel, description, titleSize, xAlign },
    hoverCardsList,
    id,
    cardsUpperLabel,
    enrollButtonLabel,
  } = content;

  const renderCard = (card, index, hovered, handleCardClick) => {
    const { image, title, contentTitle, contentDescription, buttonLabel } = card;
    return (
      <Card
        key={index}
        onMouseEnter={() => isDesktop && setIsHovered(index)}
        onMouseLeave={() => isDesktop && setIsHovered(null)}
        onClick={handleCardClick}
        $isHovered={hovered}
      >
        <ImageWrapper>
          <Image
            id={`hover-cards-image-${index}`}
            src={getImgSrcWorkaround(image?.data?.attributes?.url)}
            alt={image?.alternativeText ?? ''}
            objectFit="contain"
            width={276}
            height={173}
          />
        </ImageWrapper>
        <CardInner>
          <RichText as="h2">
            <MarkdownRichText elements={['a', 'bold', 'strong', 'b']}>{title}</MarkdownRichText>
          </RichText>
          <ArrowSVG />
        </CardInner>
        <CardHoverContent $isHovered={hovered}>
          <CardHoverInner $isHovered={hovered}>
            {(isTablet || isMobile) && hovered && (
              <CloseButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsHovered(null);
                }}
              >
                <IconCloseSVG />
              </CloseButton>
            )}
            <RichText as="h2">
              <MarkdownRichText elements={['a', 'bold', 'strong', 'b']}>
                {contentTitle}
              </MarkdownRichText>
            </RichText>
            {contentDescription && <RichText as="Paragraph">{contentDescription}</RichText>}
            <EnrollButton
              id={`hover-cards-enroll-${index}`}
              isActive={client.isActive}
              buttonLocation="hover_cards"
              openModal={() =>
                openModal({ buttonText: buttonLabel, buttonLocation: 'hover_cards' })
              }
              client={client}
              buttonLabel={buttonLabel}
              product={client?.product}
              redirectToApp={client?.redirectToApp}
            />
          </CardHoverInner>
        </CardHoverContent>
      </Card>
    );
  };

  return (
    <StyledContainer id={`hover-card-section-${id}`} xAlign="center">
      <HeaderWrapper>
        <SectionHeader
          title={<MarkdownRichText elements={['strong', 'bold']}>{title}</MarkdownRichText>}
          label={upperLabel}
          description={description ?? ''}
          xAlign={xAlign}
          titleSeoLevel="h1"
          titleSize={titleSize}
        />
        {cardsUpperLabel && (
          <RichText as="p">
            <MarkdownRichText elements={['a', 'bold', 'strong', 'b']}>
              {cardsUpperLabel}
            </MarkdownRichText>
          </RichText>
        )}
      </HeaderWrapper>
      {isMobile ? (
        <SliderWrapper>
          <StyledSlider
            loop
            pagination
            centeredSlides
            initialSlide={1}
            spaceBetween={16}
            slidesPerView={'auto'}
            slides={hoverCardsList.map((card, index) => {
              const hovered = isHovered === index;
              const handleCardClick = () => {
                if (!hovered) setIsHovered(index);
              };
              return renderCard(card, index, hovered, handleCardClick);
            })}
          />
        </SliderWrapper>
      ) : (
        <Wrapper>
          {hoverCardsList.map((card, index) => {
            const hovered = isHovered === index;
            const handleCardClick = () => {
              if (isTablet) setIsHovered(hovered ? null : index);
            };
            return renderCard(card, index, hovered, handleCardClick);
          })}
        </Wrapper>
      )}
      <ButtonWrapper>
        <EnrollButton
          id={`hover-cards-section-${id}`}
          isActive={client?.isActive}
          buttonLocation="hover_cards_section"
          openModal={() =>
            openModal({ buttonText: enrollButtonLabel, buttonLocation: 'hover_cards_section' })
          }
          client={client}
          buttonLabel={enrollButtonLabel ?? 'Get started'}
          product={client?.product}
          redirectToApp={client?.redirectToApp}
        />
      </ButtonWrapper>
    </StyledContainer>
  );
};

HoverCardsSection.defaultConfig = {
  rounded: false,
  colored: false,
  spaceBottom: true,
  spaceTop: false,
  marginBottom: false,
  withGradient: false,
};

export default HoverCardsSection;
